import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { KTSVG } from '../../../_metronic/helpers'
import { useUpdateUserCCRequestMutation } from '../../../services/authApi'
import { constraintConfig } from '../../../constraintConfig'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import CreditCardRequestStatusComp from '../../../components/StatusComp/CreditCardRequestStatusComp'

type Props = {
    show: boolean
    handleClose: () => void
    data: any
    onSuccess?: () => void
}

const ApproveRejectCorpCCRequestModal = ({ show, handleClose, data, onSuccess }: Props) => {
    const [comments, setComments] = useState('')
    const [action, setAction] = useState<'approve' | 'reject' | null>(null)
    const [updateUserCCRequest, { isLoading }] = useUpdateUserCCRequestMutation()

    useEffect(() => {
        if (show) {
            setComments('')
            setAction(null)
        }
    }, [show, data])

    const statusId =
        data?.requestccCardStatus?.id ??
        data?.creditCardCardRequest?.id ??
        data?.creditCardRequestStatusId

    const submit = async (next: 'approve' | 'reject') => {
        if (next === 'reject' && !comments.trim()) {
            toast.error('Comments are required when rejecting')
            return
        }
        const orgUserId = data?.orgUserId ?? data?.userAccountID
        if (!orgUserId) {
            toast.error('User id is missing for this request')
            return
        }
        const status =
            next === 'approve'
                ? constraintConfig.ccCardRequestStatus.RequestApproved
                : constraintConfig.ccCardRequestStatus.RequestRejected

        try {
            await updateUserCCRequest([
                {
                    orgId: orgUserId,
                    orgUserCreditCardRequestId: data?.orgUserCreditCardRequestId || null,
                    requestccCardStatus: true,
                    creditCardCardRequest: {
                        id: status,
                        title: next === 'approve' ? 'RequestApproved' : 'RequestRejected',
                    },
                    comments: comments.trim() || null,
                },
            ]).unwrap()
            toast.success(next === 'approve' ? 'Corporate CC request approved' : 'Corporate CC request rejected')
            onSuccess?.()
            handleClose()
        } catch (e: any) {
            toast.error(e?.data?.message || 'Failed to update Corporate CC request')
        }
    }

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
            <Modal.Header className="bg-light-primary">
                <Modal.Title className="fs-5">
                    Approve / Reject Corporate Credit Card Request
                </Modal.Title>
                <div className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={handleClose}>
                    <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-3 mb-4">
                    <div className="col-md-6">
                        <div className="fs-8 text-muted">Name</div>
                        <div className="fw-bold">
                            {[data?.firstName, data?.lastName].filter(Boolean).join(' ') || 'N/A'}
                        </div>
                        <div className="fs-8 text-muted">{data?.email || data?.userEmail || ''}</div>
                    </div>
                    <div className="col-md-6">
                        <div className="fs-8 text-muted">Status</div>
                        <CreditCardRequestStatusComp
                            statusId={statusId}
                            statusTitle={data?.requestccCardStatus?.title || data?.creditCardCardRequest?.title}
                        />
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Division</div>
                        <div className="fw-semibold">{data?.division?.title || data?.divisionName || 'N/A'}</div>
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Department</div>
                        <div className="fw-semibold">{data?.department?.title || data?.departmentName || 'N/A'}</div>
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Designation</div>
                        <div className="fw-semibold">{data?.designation?.title || data?.designationName || 'N/A'}</div>
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Corp CC Limit</div>
                        <div className="fw-semibold">
                            {data?.spendingLimit != null ? <CurrencyComp amount={data.spendingLimit} /> : 'N/A'}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Requested Date</div>
                        <div className="fw-semibold">
                            {data?.ccRequestedDate ? <DateTimeComp formattedDate={data.ccRequestedDate} /> : 'N/A'}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="fs-8 text-muted">Approver</div>
                        <div className="fw-semibold">{data?.ccAssignedApproverName || 'N/A'}</div>
                    </div>
                    <div className="col-12">
                        <div className="fs-8 text-muted">Ship To Address</div>
                        <div className="fw-semibold">{data?.shipToAddress || data?.findAddress || 'N/A'}</div>
                    </div>
                    <div className="col-12">
                        <div className="fs-8 text-muted mb-1">Existing Comments</div>
                        <div className="border rounded p-3 bg-light fs-7" style={{ whiteSpace: 'pre-wrap', minHeight: 48 }}>
                            {data?.comments?.trim() || 'No comments yet'}
                        </div>
                    </div>
                    <div className="col-12">
                        <label className="form-label fw-bold required">New Comments</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add comments (required for reject; appended to existing comments)"
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={handleClose} disabled={isLoading}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-danger"
                    disabled={isLoading || statusId !== constraintConfig.ccCardRequestStatus.Requested}
                    onClick={() => {
                        setAction('reject')
                        void submit('reject')
                    }}
                >
                    {isLoading && action === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
                <button
                    type="button"
                    className="btn btn-success"
                    disabled={isLoading || statusId !== constraintConfig.ccCardRequestStatus.Requested}
                    onClick={() => {
                        setAction('approve')
                        void submit('approve')
                    }}
                >
                    {isLoading && action === 'approve' ? 'Approving…' : 'Approve'}
                </button>
            </Modal.Footer>
        </Modal>
    )
}

export default ApproveRejectCorpCCRequestModal
